import { useState, useRef, useEffect } from 'react'
import { TFunction } from 'react-i18next'
import { FormApi } from 'final-form'
import useMapStore from '../../stores/useMapStore'
import useLoadingStore from '../../stores/useLoadingStore'
import { useTranslation } from 'react-i18next'

export type TMapLayer = {
    layerUrl?: string
    layerType?: string
}

export const validate = async (values: TMapLayer, t: TFunction<'translation'>, externalMapCount: number) => {
    const errors: Partial<TMapLayer> = {}
    const regUrl =
        /https?:\/\/(www\.)?[-a-zA-Z0-9@:%._\+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b([-a-zA-Z0-9()@:%_\+.~#?&//=]*)/
    if (!values.layerUrl) {
        errors.layerUrl = `${t('LABEL.TEXT_REQUIRED')}` as any
    } else if (!values.layerUrl.match(regUrl)) {
        errors.layerUrl = `${t('VALIDATE.MESSAGES.URL')}`
    }
    return errors
}

const MAP_LAYER_LIMIT: number = 5

const useMapLayer = () => {
    const formRef = useRef<FormApi<TMapLayer, Partial<TMapLayer>>>()
    const [openMapLayer, setOpenMapLayer] = useState(true)

    const [openMapLayerSetting, setOpenMapLayerSetting] = useState(false)
    const [openMapHelp, setOpenMapHelp] = useState(false)
    const [openDialogError, setOpenDialogError] = useState<boolean>(false)
    const [messageError, setMessageError] = useState<string>('')

    const { t } = useTranslation()

    const [selectedCustomLayerItem, setSelectedCustomLayerItem] = useState<MapCustomLayer | undefined>(undefined)

    const mapStore = useMapStore()
    const loadingStore = useLoadingStore()

    const handleSubmit = (values: TMapLayer) => {
        if (values.layerUrl && values.layerType) {
            if (mapStore.externalCustomMapLayers.length < MAP_LAYER_LIMIT) {
                mapStore.insertCustomMapLayer(
                    values.layerUrl,
                    values.layerType as MapCustomLayerType,
                    handleCallbackInsertMap,
                )
                loadingStore.setLoading(true)
            } else {
                setMessageError(
                    t('LABEL.MAP.MAP_LAYER.ERROR.MAXIMUM_LAYER_EXCEED').replace(
                        '{MAX_LAYER}',
                        MAP_LAYER_LIMIT.toString(),
                    ),
                )
                setOpenDialogError(true)
            }
            if (formRef){
                formRef.current?.resetFieldState('layerUrl')
            }
        }
    }

    const handleSettingExternalMapUrl = (item: MapCustomLayer) => {
        setSelectedCustomLayerItem(item)
        setOpenMapLayerSetting(true)
    }

    const handleDeleteExternalMapUrl = (item: MapCustomLayer) => {
        mapStore.removeCustomMapLayer(item)
    }

    const handleCloseMapLayer = () => {
        setOpenMapLayer(false)
    }

    const handleCloseMapLayerSetting = () => {
        setOpenMapLayerSetting(false)
        setSelectedCustomLayerItem(undefined)
    }

    const handleOpenMapHelp = () => {
        setOpenMapHelp(true)
    }

    const handleCloseMapHelp = () => {
        setOpenMapHelp(false)
    }

    const handleVisibleLayerChange = (item: MapCustomLayer, visible: boolean) => {
        item.visible = visible
        mapStore.setVisibleCustomMapLayer(item, visible)
        // if (visible) {
        //     mapStore.zoomLayerDefaultExtent(item.id)
        // }
        setSelectedCustomLayerItem(item)
    }

    const handleOpacityLayerChange = (item: MapCustomLayer, opacity: number) => {
        item.opacity = opacity
        mapStore.setOpacityCustomMapLayer(item, opacity)
        setSelectedCustomLayerItem(item)
    }

    const handleVisibleSubLayerChange = (item: MapCustomLayer, subLayerId: number, visible: boolean) => {
        if (item.subLayers) {
            let findSubLayer = item.subLayers.find((subLayer) => subLayer.id === subLayerId)
            if (findSubLayer) {
                findSubLayer.visible = visible
                item.subLayers = item.subLayers.map((subLayer) =>
                    subLayer.id === subLayerId ? findSubLayer : subLayer,
                ) as MapCustomSubLayer[]
            }
        }

        mapStore._setVisibleCustomMapSubLayer(item, subLayerId, visible)
        setSelectedCustomLayerItem(item)
    }

    const handleCallbackInsertMap = (result: MapCustomCreateResult) => {
        if (result.message === 'success') {
            if (result.item) {
                setSelectedCustomLayerItem(result.item as MapCustomLayer)
                //mapStore.zoomLayerDefaultExtent(result.item?.id)
                setOpenMapLayerSetting(true)
            }
            formRef.current?.reset()
        } else {
            setOpenDialogError(true)
            setMessageError(t('LABEL.MAP.MAP_LAYER.ERROR.MAP_CREATE_ERROR'))
        }
        loadingStore.setLoading(false)
    }

    const handleDialogErrorOKClick = () => {
        setOpenDialogError(false)
        setMessageError('')
    }

    useEffect(() => {
        return () => {
            mapStore.clearReportDataResult()
        }
    }, [])

    return {
        onSubmit: handleSubmit,
        customMapLayerItem: selectedCustomLayerItem,
        internalCustomMapLayers: mapStore.internalCustomMapLayers,
        externalCustomMapLayers: mapStore.externalCustomMapLayers,
        onSettingExternalMapUrl: handleSettingExternalMapUrl,
        onDeleteExternalMapUrl: handleDeleteExternalMapUrl,
        formRef,
        openMapLayer,
        openMapLayerSetting,
        openMapHelp,
        openDialogError,
        messageError,
        onOpenMapHelp: handleOpenMapHelp,
        onCloseMapLayer: handleCloseMapLayer,
        onCloseMapLayerSetting: handleCloseMapLayerSetting,
        onCloseMapHelp: handleCloseMapHelp,
        onDialogErrorOKClick: handleDialogErrorOKClick,
        onOpacityLayerChange: handleOpacityLayerChange,
        onVisibleLayerChange: handleVisibleLayerChange,
        onVisibleSublayerChange: handleVisibleSubLayerChange,
    }
}

export default useMapLayer
